import React, { useState, useMemo } from 'react'
import { Button, Input, Spin, message, Radio, Modal, Select, Alert, Icon, Popover } from 'antd'
import { api } from '../../../../../../../../../services'
import { useTranslation } from 'react-i18next'
import { ImagePreview, Builder } from '../../../../../../../../../components'
import { useDispatch } from 'react-redux'
import actions from '../../../../../../../../../store/actions'
import * as lessonBuilderActions from '../../../../../../../../../store/actions/lessonBuilder'
import nanoid from 'nanoid'
import './infographics.scss'

const { TextArea } = Input

const Infographics = ({
  onCancel,
  extractedText,
  previewData,
  pages,
  currentPage,
  lessonUpdate,
  setPreviewData,
  backgroundImageLink = '',
  backgroundImageId = '',
  backgroundImageOpacity = 0,
  backgroundColor = ''
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [customPrompt, setCustomPrompt] = useState('')
  const [generatedImage, setGeneratedImage] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showActionModal, setShowActionModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState('replace')
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [selectedPagesForAction, setSelectedPagesForAction] = useState([])
  const [previewPageIndex, setPreviewPageIndex] = useState(null)
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false)
  const [selectedImageUid, setSelectedImageUid] = useState(null)

  const { handleLessonInput } = actions.lessonBuilder

  // Aspect ratio options
  const aspectRatioOptions = [
    { label: t('infographic:square'), value: '1:1' },
    { label: t('infographic:portrait'), value: '2:3' },
    { label: t('infographic:landscape'), value: '3:2' },
    { label: t('infographic:three_four_Portrait'), value: '3:4' },
    { label: t('infographic:four_three_landscape'), value: '4:3' },
    { label: t('infographic:four_five_portrait'), value: '4:5' },
    { label: t('infographic:four_five_landscape'), value: '5:4' },
    { label: t('infographic:mobile_portrait'), value: '9:16' },
    { label: t('infographic:widescreen'), value: '16:9' },
    { label: t('infographic:ultrawide'), value: '21:9' }
  ]

  // Get selected pages info for action modal
  const selectedPagesInfo = useMemo(() => {
    return previewData
      .map((page, index) => ({ page, index }))
      .filter(({ page }) => page.pageSelected)
  }, [previewData])

  // Calculate image dimensions based on aspect ratio for full-width display
  const calculateImageDimensions = (aspectRatio) => {
    const containerWidth = 1023
    const containerHeight = 552

    // Parse aspect ratio (e.g., "16:9" -> [16, 9])
    const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number)

    // Calculate height for full width
    const calculatedHeight = (containerWidth * heightRatio) / widthRatio

    // If calculated height exceeds container, fit by height instead
    if (calculatedHeight > containerHeight) {
      const calculatedWidth = (containerHeight * widthRatio) / heightRatio
      return {
        width: Math.round(calculatedWidth),
        height: containerHeight,
        x: Math.round((containerWidth - calculatedWidth) / 2), // Center horizontally
        y: 0
      }
    }

    // Fit by width
    return {
      width: containerWidth,
      height: Math.round(calculatedHeight),
      x: 0,
      y: Math.round((containerHeight - calculatedHeight) / 2) // Center vertically
    }
  }

  const generateInfographic = async () => {
    const hasCustomPrompt = customPrompt?.trim()
    const hasExtractedText = extractedText?.trim()

    if (!hasExtractedText && !hasCustomPrompt) {
      message.error(t('infographic:no_content'))
      return
    }

    setIsGenerating(true)

    try {
      const response = await api.imageGeneration.generateNanoBananaImage({
        generate_infographic: true,
        course_title: 'Infographic',
        course_content: extractedText || '',
        aspect_ratio: aspectRatio,
        user_prompt: customPrompt || '',
      })

      if (response && response.data) {
        // Check if response is a blob (direct image)
        // Blob detection: check if it's a Blob, or if it has blob-like properties
        const isBlob = response.data instanceof Blob ||
          (response.data.type && typeof response.data.size === 'number' && !response.data.body);

        if (isBlob) {
          // Check if the blob is actually a JSON error
          if (response.data.type === 'application/json') {
            const text = await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsText(response.data)
            })

            try {
              const json = JSON.parse(text)

              // Handle new format with direct URL
              if (json.url) {
                try {
                  const imgResponse = await fetch(json.url)
                  const blob = await imgResponse.blob()
                  const reader = new FileReader()
                  reader.onloadend = () => {
                    setGeneratedImage(reader.result)
                    message.success(t('infographic:generated'))
                  }
                  reader.onerror = () => {
                    throw new Error('Failed to read image data')
                  }
                  reader.readAsDataURL(blob)
                  return
                } catch (err) {
                  console.error('Error fetching generated image:', err)
                  throw new Error('Failed to fetch generated image from URL')
                }
              }

              if (json.errorMessage) {
                message.error(json.errorMessage)
                return
              }
            } catch (e) {
              console.error('Failed to parse error response:', e)
            }
          }

          // Convert blob to data URL
          const reader = new FileReader()
          reader.onloadend = () => {
            setGeneratedImage(reader.result)
            message.success(t('infographic:generated'))
          }
          reader.onerror = () => {
            throw new Error('Failed to read image data')
          }
          reader.readAsDataURL(response.data)
        } else if (response.data.body || response.data.errorMessage) {
          // Handle old format (JSON with base64)
          const { body, headers, errorMessage } = response.data

          if (errorMessage) {
            message.error(errorMessage)
            return
          }

          if (!body) {
            throw new Error('No image body in response')
          }

          const contentType = headers?.['Content-Type'] || 'image/png'
          const dataUrl = `data:${contentType};base64,${body}`

          setGeneratedImage(dataUrl)
          message.success(t('infographic:generated'))
        } else {
          console.error('Unexpected response format:', response.data)
          throw new Error('Unexpected response format')
        }
      } else {
        throw new Error('No image data received')
      }
    } catch (error) {
      console.error('Error generating infographic:', error)
      message.error(
        (error && error.message) || t('infographic:generation_failed')
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleApplyClick = () => {
    if (!generatedImage) {
      message.error(t('infographic:no_image'))
      return
    }
    // Initialize with first selected page (single selection)
    const firstSelectedIndex = selectedPagesInfo.length > 0 ? selectedPagesInfo[0].index : null
    setSelectedPagesForAction(firstSelectedIndex !== null ? [firstSelectedIndex] : [])
    setShowActionModal(true)
  }

  const triggerUpdate = async (data, updatedPages) => {
    const pagesToUpdate = updatedPages || pages
    setPreviewData && setPreviewData(data)
    await dispatch(handleLessonInput('pages_landscape', data))
    await dispatch(handleLessonInput('pages', pagesToUpdate))

    if (lessonUpdate) {
      await lessonUpdate({ pages_landScape: data, pages: pagesToUpdate })
    }
    onCancel()
  }

  const handleInfographicsApply = imageData => {
    // replace existing image, add new image, or create new page
    // imageData is an object with { id, link, action }

    if (!imageData || !imageData.id || !imageData.link) {
      console.error('Invalid image data provided to handleInfographicsApply')
      return
    }

    const { id, link, action } = imageData

    let newData = []
    let newPages = [...pages]

    if (action === 'newPage') {
      // Find the last selected page index
      const lastSelectedIndex = previewData.reduce(
        (lastIndex, page, index) => (page.pageSelected ? index : lastIndex),
        -1
      )

      if (lastSelectedIndex === -1) {
        message.error(t('infographic:no_page_selected'))
        return
      }

      // Get logo from first page if exists
      const firstPageData = previewData[0]
      const logoItem = firstPageData?.data?.find(
        item => item.type === 'IMAGE' && item.display === 'LOGO'
      )

      // Create background item
      const backgroundItem = backgroundImageId
        ? {
          uid: nanoid(8),
          type: 'BACKGROUND_IMAGE',
          params: {
            content: backgroundImageLink,
            content_id: backgroundImageId,
            opacity: backgroundImageOpacity
          }
        }
        : {
          uid: nanoid(8),
          type: 'BACKGROUND_COLOR',
          display: 'BACKGROUND_COLOR',
          params: {
            content: backgroundColor,
            content_id: null,
            opacity: 1
          }
        }

      // Calculate dimensions for full-width display
      const imageDimensions = calculateImageDimensions(aspectRatio)

      // Create new page with infographic
      const newPageLandscape = {
        size: { width: 1024, height: 768 },
        orientation: 'landscape',
        data: [
          {
            uid: nanoid(8),
            type: 'IMAGE',
            display: 'IMAGE',
            params: {
              content: link,
              content_id: id,
              x: imageDimensions.x,
              y: imageDimensions.y,
              width: imageDimensions.width,
              height: imageDimensions.height
            }
          },
          backgroundItem,
          logoItem
        ].filter(Boolean)
      }

      const newPagePortrait = {
        data: [],
        orientation: 'portrait',
        size: { width: 1024, height: 768 }
      }

      // Insert new page after last selected page
      const insertPosition = lastSelectedIndex + 1
      newData = [
        ...previewData.slice(0, insertPosition),
        newPageLandscape,
        ...previewData.slice(insertPosition)
      ]
      newPages = [
        ...pages.slice(0, insertPosition),
        newPagePortrait,
        ...pages.slice(insertPosition)
      ]
    } else if (action === 'replace') {
      newData = previewData.map((page, index) => {
        // Only apply to pages selected for this action
        if (selectedPagesForAction.includes(index)) {
          // Check if page has any IMAGE elements to replace
          const hasImage =
            page.data &&
            page.data.some(
              item => item.type === 'IMAGE'
            )

          if (hasImage) {
            return {
              ...page,
              data: page.data.map(item => {
                // If selectedImageUid is set, only replace that specific image
                if (item.type === 'IMAGE') {
                  if (selectedImageUid) {
                    // Replace only if this is the selected image
                    if (item.uid === selectedImageUid) {
                      return {
                        ...item,
                        params: {
                          ...item.params,
                          content: link,
                          content_id: id
                        }
                      }
                    }
                  } else {
                    // No specific image selected, replace all images (old behavior)
                    return {
                      ...item,
                      params: {
                        ...item.params,
                        content: link,
                        content_id: id
                      }
                    }
                  }
                }
                return item
              })
            }
          } else {
            // No image to replace, so add new image instead
            return {
              ...page,
              data: (page.data || []).concat({
                uid: nanoid(8),
                type: 'IMAGE',
                display: 'IMAGE',
                params: {
                  content: link,
                  content_id: id,
                  x: 0,
                  y: 0,
                  width: 300,
                  height: 300
                }
              })
            }
          }
        }
        return page
      })
    } else {
      // action === 'add'
      newData = previewData.map((page, index) => {
        // Only apply to pages selected for this action
        if (selectedPagesForAction.includes(index)) {
          return {
            ...page,
            data: (page.data || []).concat({
              uid: nanoid(8),
              type: 'IMAGE',
              display: 'IMAGE',
              params: {
                content: link,
                content_id: id,
                x: 0,
                y: 0,
                width: 300,
                height: 300
              }
            })
          }
        }
        return page
      })
    }

    triggerUpdate(newData, newPages)
  }

  const handleConfirmApply = () => {
    if (!generatedImage) {
      message.error(t('infographic:no_image'))
      return
    }

    // If action is 'replace', check if the selected page has multiple images
    if (selectedAction === 'replace' && selectedPagesForAction.length > 0) {
      const pageIndex = selectedPagesForAction[0]
      const selectedPage = previewData[pageIndex]

      // Get all IMAGE elements (excluding LOGO)
      const imageElements = selectedPage?.data?.filter(
        item => item.type === 'IMAGE'
      ) || []

      // If multiple images, show image selection modal
      if (imageElements.length > 1) {
        setShowImageSelectionModal(true)
        return
      }

      // If single image or no images, proceed normally
      setSelectedImageUid(imageElements.length === 1 ? imageElements[0].uid : null)
    }

    handleApply(generatedImage, selectedAction)
  }

  const getImageAsUploadedData = async dataUrl => {
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl)
      const blob = await response.blob()

      // Convert blob to base64
      const base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = error => reject(error)
        reader.readAsDataURL(blob)
      })

      const filename = `infographic-${Date.now()}.png`
      const fileData = {
        name: filename,
        filename: filename,
        title: filename,
        size: blob.size,
        croppedImage: base64String,
        type: blob.type || 'image/png',
        lastModifiedDate: new Date()
      }

      return fileData
    } catch (error) {
      console.error('Error converting image:', error)
      throw error
    }
  }

  const handleApply = async (image, action) => {
    if (!image) {
      message.error(t('infographic:no_image'))
      return
    }

    setIsUploading(true)

    try {
      // Convert data URL to file format
      const fileData = await getImageAsUploadedData(image)

      // Upload the image
      const uploadResponse = await dispatch(actions.library.upload(fileData))

      if (uploadResponse && uploadResponse.id && uploadResponse.link) {
        // Call handleInfographicsApply with the uploaded image data and action
        handleInfographicsApply({
          id: uploadResponse.id,
          link: uploadResponse.link,
          action
        })
        message.success(t(`infographic:${action}_success`))
      } else {
        throw new Error('Upload failed: Invalid response')
      }
    } catch (error) {
      console.error('Error uploading infographic:', error)
      message.error((error && error.message) || t('infographic:upload_failed'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancelActionModal = () => {
    setShowActionModal(false)
    setSelectedAction('replace')
    setSelectedPagesForAction([])
  }

  const handleConfirmImageSelection = () => {
    if (!selectedImageUid) {
      message.error(t('infographic:select_image_to_replace') || 'Please select an image to replace')
      return
    }
    setShowImageSelectionModal(false)
    handleApply(generatedImage, selectedAction)
  }

  const handleCancelImageSelection = () => {
    setShowImageSelectionModal(false)
    setSelectedImageUid(null)
  }

  const handleCancel = () => {
    setCustomPrompt('')
    setGeneratedImage(null)
    setSelectedAction('replace')
    setAspectRatio('1:1')
    setShowActionModal(false)
    setSelectedPagesForAction([])
    setShowImageSelectionModal(false)
    setSelectedImageUid(null)
    onCancel()
  }

  return (
    <>
      <Modal
        visible
        onCancel={handleCancel}
        footer={null}
        width={1000}
        centered
        title={t('infographic:title')}
        destroyOnClose
        maskClosable={false}
        className="infographics-modal" // Added class
      >
        <div className="infographic-modal-content">
          {/* Info Alert */}
          {selectedPagesInfo.length > 0 && (
            <Alert
              message={t('infographic:pages_selected', { count: selectedPagesInfo.length }) || `${selectedPagesInfo.length} page(s) selected`}
              description={t('infographic:content_from_selected_pages') || 'Content from all selected pages will be used to generate the infographic.'}
              type="info"
              showIcon
              className="infographic-info-alert"
            />
          )}

          <div className="infographic-layout">
            {/* Left Column: Controls */}
            <div className="infographic-left-column">

              {/* Aspect Ratio */}
              <div className="form-item"> {/* Added class */}
                <label className="infographic-label">
                  {t('infographic:aspect_ratio') || 'Aspect Ratio'}
                </label>
                <Select
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  disabled={isGenerating}
                  className="infographic-select"
                  size="large"
                  dropdownClassName="infographic-select-dropdown"
                >
                  {aspectRatioOptions.map(ratio => (
                    <Select.Option key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {/* Custom Instructions */}
              <div className="form-item"> {/* Added class */}
                <label className="infographic-label">
                  {t('v5:infographic_custom_instructions')}
                  <span className="infographic-label-optional">(Optional)</span>
                </label>
                <TextArea
                  placeholder={t('v5:infographic_prompt_placeholder_new')}
                  value={customPrompt}
                  onChange={e => setCustomPrompt(e.target.value)}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  disabled={isGenerating}
                  className="infographic-textarea"
                />
              </div>

              <Button
                type="primary"
                size="large"
                onClick={generateInfographic}
                loading={isGenerating}
                disabled={selectedPagesInfo.length === 0 && !customPrompt?.trim()}
                block
                className="infographic-generate-btn"
              >
                {isGenerating
                  ? t('infographic:generating')
                  : generatedImage
                    ? t('infographic:regenerate')
                    : t('v5:infographic_generate_btn')}
              </Button>
            </div>

            {/* Right Column: Preview */}
            <div className="infographic-right-column">
              <div className="infographic-preview-container">
                {isGenerating ? (
                  <div className="infographic-generating">
                    <Spin size="large" />
                    <p>{t('v5:infographic_creating_magic')}</p>
                  </div>
                ) : generatedImage ? (
                  <div className="infographic-preview-wrapper">
                    <ImagePreview
                      src={generatedImage}
                      alt="Generated Infographic"
                      className="infographic-preview-image"
                    />
                    <div className="infographic-preview-actions">
                      <Button onClick={() => setGeneratedImage(null)}>
                        {t('v5:infographic_discard')}
                      </Button>
                      <Button type="primary" onClick={handleApplyClick}>
                        {t('v4:apply_changes')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="infographic-empty-state">
                    <div className="emoji">🎨</div>
                    <h3>
                      {t('v5:infographic_preview_area')}
                    </h3>
                    <p>
                      {t('v5:infographic_preview_desc')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="infographic-modal-footer">
            <Button onClick={handleCancel} type="text">
              {t('buttons:cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Action Selection Modal */}
      <Modal
        visible={showActionModal}
        onCancel={handleCancelActionModal}
        footer={null}
        width={500}
        centered
        title={t('infographic:apply_question')}
      >
        <div className="infographic-action-modal-content">
          <Radio.Group
            value={selectedAction}
            onChange={e => setSelectedAction(e.target.value)}
            className="infographic-radio-group"
          >
            <div className="radio-options">
              <Radio value="replace">
                <div className="radio-label">
                  <div className="label-title">
                    {t('infographic:replace_image')}
                  </div>
                  <div className="label-description">
                    {t('infographic:replace_existing_images_on_selected_pages')}
                  </div>
                </div>
              </Radio>

              {/* Page Selection for Replace action - shown inline */}
              {selectedAction === 'replace' && selectedPagesInfo.length > 1 && (
                <div className="infographic-page-selection">
                  <div className="selection-title">
                    {t('infographic:select_page_to_apply') || 'Select page to apply:'}
                  </div>
                  <div className="page-grid">
                    {selectedPagesInfo.map(({ page, index }) => {
                      const isSelected = selectedPagesForAction.includes(index)
                      return (
                        <div
                          key={index}
                          onClick={() => {
                            // Single selection: replace the array with only this index
                            setSelectedPagesForAction([index])
                          }}
                          className={`infographic-page-card ${isSelected ? 'selected' : ''}`}
                        >
                          {/* Radio indicator */}
                          <div className={`checkbox-indicator ${isSelected ? 'checked' : ''}`}>
                            {isSelected && <Icon type="check" />}
                          </div>

                          {/* Page thumbnail */}
                          <div className="page-thumbnail-wrapper">
                            <div className="thumbnail-preview">
                              <Builder.Page
                                pageStructure={page.data || []}
                                isPreview
                              />
                            </div>
                          </div>

                          {/* Page label */}
                          <div className={`page-label ${isSelected ? 'selected' : ''}`}>
                            {t('infographic:page_number', { number: index + 1 }) || `Page ${index + 1}`}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              <Radio value="add">
                <div className="radio-label">
                  <div className="label-title">
                    {t('infographic:add_to_pages')}
                  </div>
                  <div className="label-description">
                    {t('infographic:add_image_to_selected_pages')}
                  </div>
                </div>
              </Radio>

              {/* Page Selection for Add action - shown inline */}
              {selectedAction === 'add' && selectedPagesInfo.length > 1 && (
                <div className="infographic-page-selection">
                  <div className="selection-title">
                    {t('infographic:select_page_to_apply') || 'Select page to apply:'}
                  </div>
                  <div className="page-grid">
                    {selectedPagesInfo.map(({ page, index }) => {
                      const isSelected = selectedPagesForAction.includes(index)
                      return (
                        <div
                          key={index}
                          onClick={() => {
                            // Single selection: replace the array with only this index
                            setSelectedPagesForAction([index])
                          }}
                          className={`infographic-page-card ${isSelected ? 'selected' : ''}`}
                        >
                          {/* Radio indicator */}
                          <div className={`checkbox-indicator ${isSelected ? 'checked' : ''}`}>
                            {isSelected && <Icon type="check" />}
                          </div>

                          {/* Page thumbnail */}
                          <div className="page-thumbnail-wrapper">
                            <div className="thumbnail-preview">
                              <Builder.Page
                                pageStructure={page.data || []}
                                isPreview
                              />
                            </div>
                          </div>

                          {/* Page label */}
                          <div className={`page-label ${isSelected ? 'selected' : ''}`}>
                            {t('infographic:page_number', { number: index + 1 }) || `Page ${index + 1}`}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              <Radio value="newPage">
                <div className="radio-label">
                  <div className="label-title">
                    {t('infographic:create_new_page')}
                  </div>
                  <div className="label-description">
                    {t('infographic:create_page_with_infographic_after_last_selected')}
                  </div>
                </div>
              </Radio>
            </div>
          </Radio.Group>

          <div className="infographic-confirm-actions">
            <Button size="large" onClick={handleCancelActionModal}>
              {t('buttons:cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleConfirmApply}
              loading={isUploading}
              disabled={isUploading}
              className="confirm-btn"
            >
              {t('buttons:confirm') || 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Image Selection Modal - shown when page has multiple images */}
      <Modal
        visible={showImageSelectionModal}
        onCancel={handleCancelImageSelection}
        footer={null}
        width={600}
        centered
        title={t('infographic:select_image_to_replace') || 'Select Image to Replace'}
      >
        <div className="infographic-image-selection-modal">
          <p className="selection-instruction">
            {t('infographic:multiple_images_found') || 'This page has multiple images. Please select which image you want to replace:'}
          </p>

          <div className="image-grid">
            {selectedPagesForAction.length > 0 && (() => {
              const pageIndex = selectedPagesForAction[0]
              const selectedPage = previewData[pageIndex]
              const imageElements = selectedPage?.data?.filter(
                item => item.type === 'IMAGE'
              ) || []

              return imageElements.map((imageItem, idx) => {
                const isSelected = selectedImageUid === imageItem.uid
                return (
                  <div
                    key={imageItem.uid}
                    onClick={() => setSelectedImageUid(imageItem.uid)}
                    className={`image-selection-card ${isSelected ? 'selected' : ''}`}
                  >
                    {/* Radio indicator */}
                    <div className={`radio-indicator ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <Icon type="check" />}
                    </div>

                    {/* Image preview */}
                    <div className="image-preview">
                      <ImagePreview
                        src={imageItem.params?.content}
                        alt={`Image ${idx + 1}`}
                        className="preview-thumbnail"
                      />
                    </div>

                    {/* Image label */}
                    <div className={`image-label ${isSelected ? 'selected' : ''}`}>
                      {t('infographic:image_number', { number: idx + 1 }) || `Image ${idx + 1}`}
                    </div>
                  </div>
                )
              })
            })()}
          </div>

          <div className="infographic-confirm-actions">
            <Button size="large" onClick={handleCancelImageSelection}>
              {t('buttons:cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleConfirmImageSelection}
              disabled={!selectedImageUid}
              className="confirm-btn"
            >
              {t('buttons:confirm') || 'Confirm'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default Infographics
